import React, {forwardRef, useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {defineMessages, useIntl} from 'react-intl'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import ModuleContainer from '../../components/module-container'
import CTA from '../../components/cms/cta'
import {ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon} from '../../components/icons'
import {hasValidString} from '../../utils/utils'
import messageBarSlotRecipe from './index.recipe'
import {getValidSlides, hasSlideWithCta} from './slides'

const labels = defineMessages({
    regionLabel: {
        id: 'leap.messageBar.regionLabel',
        defaultMessage: 'Message bar'
    },
    previousSlide: {
        id: 'leap.messageBar.previousSlide',
        defaultMessage: 'Previous slide'
    },
    nextSlide: {
        id: 'leap.messageBar.nextSlide',
        defaultMessage: 'Next slide'
    },
    pauseAutoplay: {
        id: 'leap.messageBar.pauseAutoplay',
        defaultMessage: 'Pause message carousel'
    },
    playAutoplay: {
        id: 'leap.messageBar.playAutoplay',
        defaultMessage: 'Play message carousel'
    },
    slideLabel: {
        id: 'leap.messageBar.slideLabel',
        defaultMessage: '{current} of {total}'
    }
})

// Time each slide stays visible before autoplay advances
const AUTOPLAY_INTERVAL_MS = 4000

/**
 * MessageBar CMS module for site-wide announcements and notifications.
 * Renders an accessible, rotatable message bar flush above the header.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {string} [props.moduleId] - CMS module identifier.
 * @param {string} [props.moduleType] - Module type name ('leap-message-bar').
 * @param {string} [props.pageId] - Page identifier.
 * @param {string} [props.color] - Design system color world.
 * @param {Array<Object>} [props.messages] - Messages shown as rotating slides.
 * @returns {JSX.Element|null} Rendered message bar or null when no messages exist.
 */
const MessageBar = forwardRef(
    ({moduleId, moduleType, pageId, color = 'neutral', messages = []}, ref) => {
        const intl = useIntl()
        const [{currentIndex, previousIndex}, setSlideIndices] = useState({
            currentIndex: 0,
            previousIndex: null
        })
        const [isPlaying, setIsPlaying] = useState(true)
        const [isPausedByHover, setIsPausedByHover] = useState(false)

        const validSlides = useMemo(() => getValidSlides(messages), [messages])

        const hasSlides = validSlides.length > 0
        const isMultiSlide = validSlides.length > 1
        const hasCta = hasSlideWithCta(validSlides)

        const recipe = useSlotRecipe({recipe: messageBarSlotRecipe})
        const styles = useMemo(() => recipe({color, hasCta}), [recipe, color, hasCta])

        // The outgoing slide is kept as `previousIndex` so it can animate out upwards
        // while the next slide enters from below.
        const goToSlide = useCallback((getNextIndex) => {
            setSlideIndices(({currentIndex: prev}) => ({
                currentIndex: getNextIndex(prev),
                previousIndex: prev
            }))
        }, [])

        // Reset slide index if slides change and index is out of bounds
        useEffect(() => {
            if (currentIndex >= validSlides.length) {
                setSlideIndices({currentIndex: 0, previousIndex: null})
            }
        }, [validSlides.length, currentIndex])

        // Handle autoplay timer
        useEffect(() => {
            if (!isMultiSlide || !isPlaying || isPausedByHover) {
                return
            }

            const timer = setInterval(() => {
                goToSlide((prev) => (prev + 1) % validSlides.length)
            }, AUTOPLAY_INTERVAL_MS)

            return () => clearInterval(timer)
        }, [isMultiSlide, isPlaying, isPausedByHover, validSlides.length, goToSlide])

        const handlePrev = useCallback(() => {
            goToSlide((prev) => (prev - 1 + validSlides.length) % validSlides.length)
        }, [validSlides.length, goToSlide])

        const handleNext = useCallback(() => {
            goToSlide((prev) => (prev + 1) % validSlides.length)
        }, [validSlides.length, goToSlide])

        const handleTogglePlay = useCallback(() => {
            setIsPlaying((prev) => !prev)
        }, [])

        const handleMouseEnter = useCallback(() => setIsPausedByHover(true), [])
        const handleMouseLeave = useCallback(() => setIsPausedByHover(false), [])
        const handleFocus = useCallback(() => setIsPausedByHover(true), [])
        const handleBlur = useCallback((e) => {
            if (!e.currentTarget.contains(e.relatedTarget)) {
                setIsPausedByHover(false)
            }
        }, [])

        if (!hasSlides) {
            return null
        }

        const safeIndex = currentIndex < validSlides.length ? currentIndex : 0

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                spacing="none"
                modulePadding="none"
                background="none"
                layerStyle={null}
                ref={ref}
            >
                <Box
                    css={styles.root}
                    role="region"
                    aria-label={intl.formatMessage(labels.regionLabel)}
                    aria-roledescription={isMultiSlide ? 'carousel' : undefined}
                    onMouseEnter={isMultiSlide ? handleMouseEnter : undefined}
                    onMouseLeave={isMultiSlide ? handleMouseLeave : undefined}
                    onFocus={isMultiSlide ? handleFocus : undefined}
                    onBlur={isMultiSlide ? handleBlur : undefined}
                >
                    <Box css={styles.container}>
                        {isMultiSlide && (
                            <Box css={styles.prevControl}>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handlePrev}
                                    aria-label={intl.formatMessage(labels.previousSlide)}
                                    css={styles.controlButton}
                                >
                                    <ChevronLeftIcon />
                                </Box>
                            </Box>
                        )}

                        <Box
                            css={styles.slider}
                            aria-live={isMultiSlide ? (isPlaying ? 'off' : 'polite') : undefined}
                        >
                            {validSlides.map((slide, index) => {
                                const isActive = index === safeIndex
                                const slideState = isActive
                                    ? 'active'
                                    : index === previousIndex
                                    ? 'leaving'
                                    : 'idle'

                                return (
                                    <Box
                                        key={index}
                                        css={styles.slide}
                                        data-state={slideState}
                                        aria-hidden={isActive ? undefined : true}
                                        role={isMultiSlide ? 'group' : undefined}
                                        aria-roledescription={isMultiSlide ? 'slide' : undefined}
                                        aria-label={
                                            isMultiSlide
                                                ? intl.formatMessage(labels.slideLabel, {
                                                      current: index + 1,
                                                      total: validSlides.length
                                                  })
                                                : undefined
                                        }
                                    >
                                        <Box css={styles.slideText}>
                                            <Box as="span" css={styles.copy}>
                                                {slide.copy.text}
                                            </Box>
                                            {hasValidString(slide.cta?.label) && (
                                                <CTA
                                                    {...slide.cta}
                                                    variant="text"
                                                    trackingContext={{
                                                        moduleName: moduleType,
                                                        eventLabel: 'cta'
                                                    }}
                                                    css={styles.cta}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                )
                            })}
                        </Box>

                        {isMultiSlide && (
                            <Box css={styles.controls}>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleTogglePlay}
                                    aria-label={intl.formatMessage(
                                        isPlaying ? labels.pauseAutoplay : labels.playAutoplay
                                    )}
                                    aria-pressed={!isPlaying}
                                    css={styles.controlButton}
                                >
                                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                                </Box>
                                <Box
                                    as="button"
                                    type="button"
                                    onClick={handleNext}
                                    aria-label={intl.formatMessage(labels.nextSlide)}
                                    css={styles.controlButton}
                                >
                                    <ChevronRightIcon />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    MessageBar.displayName = 'MessageBar'
}

MessageBar.propTypes = {
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    color: PropTypes.oneOf([
        'neutral',
        'cream',
        'creamDarker',
        'fjordBlue',
        'cherry',
        'pineGreen',
        'terracotta',
        'terracottaDarker',
        'malva',
        'malvaDarker',
        'sunflower',
        'black'
    ]),
    messages: PropTypes.arrayOf(
        PropTypes.shape({
            copy: PropTypes.shape({
                text: PropTypes.string
            }),
            cta: PropTypes.shape({
                label: PropTypes.string,
                action: PropTypes.object,
                openInNewTab: PropTypes.bool
            })
        })
    )
}

export default MessageBar
